import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Logo } from '../components/Logo';
import { 
  ArrowLeft, Heart, Shield, Brain, Target, Users, 
  Lightbulb, Award, CheckCircle2, Sparkles, Quote
} from 'lucide-react';
import GlobalFooter from '../components/GlobalFooter';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: Shield,
      title: 'Safety First',
      description: 'Every recommendation includes medication interaction checks and professional consultation reminders.'
    },
    {
      icon: Brain,
      title: 'Evidence-Based',
      description: 'Our suggestions are grounded in peer-reviewed research, with links to studies you can verify yourself.'
    },
    {
      icon: Heart,
      title: 'Personalized Care',
      description: 'No two bodies are alike. Our AI considers your unique health profile, lifestyle, and goals.'
    },
    {
      icon: Users,
      title: 'Transparency',
      description: 'We\'re upfront about what AI can and cannot do. Your data stays yours—never sold, never used for training.'
    }
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Complete Your Assessment',
      description: 'Answer questions about your health goals, current medications, lifestyle, and any symptoms you\'re experiencing.'
    },
    {
      step: '02',
      title: 'AI Analysis',
      description: 'Our AI (GPT-5.2) analyzes your responses, checks for potential interactions, and generates personalized recommendations.'
    },
    {
      step: '03',
      title: 'Review Your Protocol',
      description: 'Receive a detailed supplement protocol with dosages, timing, and evidence-based explanations for each suggestion.'
    },
    {
      step: '04',
      title: 'Consult & Track',
      description: 'Share your results with your healthcare provider, then use our journaling tools to track your progress over time.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="default" linkTo="/" />
          <div className="w-16 sm:w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4">About Vitala</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Personalized supplement guidance powered by AI, designed to help you make informed decisions about your health.
          </p>
        </motion.div>

        {/* Mission Statement - Clinical Intelligence, Zero Agenda */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 sm:p-8 border border-border mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <Target className="w-6 h-6 text-primary" />
            <h2 className="text-xl sm:text-2xl font-semibold text-[#1A1A1A]">The Vitala Mission: Clinical Intelligence, Zero Agenda</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We believe the modern wellness industry has a trust problem. Too many supplements are sold through hype, 
            fear, and influencer marketing—not science. <strong>Vitala exists to change that.</strong>
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Our platform uses clinically-informed AI to analyze your unique health profile, cross-reference your 
            medications, and recommend only what the evidence supports. We don't sell products. We don't take 
            kickbacks. We don't care which brand you buy—we only care that you're buying the right thing, 
            in the right form, at the right dose.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Whether you're managing a chronic condition, optimizing performance, or just trying to fill the gaps 
            in your modern diet, Vitala gives you the clarity to make confident, informed decisions.
          </p>
        </motion.section>

        {/* Founder's Corner */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-6 sm:p-8 border border-primary/20 mb-8 relative"
        >
          <div className="absolute top-4 right-4 opacity-10">
            <Quote className="w-16 h-16 text-primary" />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-xl sm:text-2xl font-semibold text-[#1A1A1A]">Founder's Corner</h2>
          </div>
          <div className="space-y-4 text-muted-foreground leading-relaxed italic">
            <p>
              "I started Vitala after watching people I loved spend thousands on supplements that did nothing—or worse, 
              conflicted with their medications. The wellness industry profits from confusion. We wanted to build 
              something different: a tool that tells you the truth, even when the truth is 'you don't need this.'"
            </p>
            <p>
              "Our AI isn't here to sell you a subscription box. It's here to give you the same research-backed 
              guidance that used to be reserved for people with access to functional medicine specialists. 
              Everyone deserves that."
            </p>
          </div>
          <p className="mt-4 text-sm font-semibold text-[#1A1A1A]">— The Vitala Team</p>
        </motion.section>

        {/* Our Values */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="text-xl sm:text-2xl font-semibold text-[#1A1A1A] mb-6 text-center">Our Values</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-5 border border-border">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <value.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-[#1A1A1A]">{value.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground">{value.description}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* How It Works */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-6 sm:p-8 border border-border mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <Lightbulb className="w-6 h-6 text-primary" />
            <h2 className="text-xl sm:text-2xl font-semibold text-[#1A1A1A]">How Vitala Works</h2>
          </div>
          <div className="space-y-6">
            {howItWorks.map((item, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-bold text-primary">{item.step}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-[#1A1A1A] mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* AI Transparency */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-blue-50 border border-blue-200 rounded-2xl p-6 sm:p-8 mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <Brain className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl sm:text-2xl font-semibold text-blue-900">About Our AI</h2>
          </div>
          <div className="space-y-4 text-blue-800">
            <p className="text-sm sm:text-base">
              Vitala uses <strong>GPT-5.2</strong> via the Emergent LLM platform to analyze your health profile 
              and generate personalized recommendations.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm"><strong>Stateless Processing:</strong> Your data is only sent to the AI at the moment of analysis. We don't use your information to train AI models.</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm"><strong>Educational Tool:</strong> Our AI provides informational suggestions, not medical diagnoses or prescriptions.</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm"><strong>Human Oversight Required:</strong> Always consult a healthcare professional before starting any supplement regimen.</p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Important Notice */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-amber-50 border border-amber-200 rounded-2xl p-6 sm:p-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <Award className="w-6 h-6 text-amber-600" />
            <h2 className="text-xl sm:text-2xl font-semibold text-amber-900">Our Commitment</h2>
          </div>
          <p className="text-amber-800 text-sm sm:text-base mb-4">
            We're committed to providing you with the best possible guidance while being completely transparent 
            about our limitations. Vitala is a tool to help inform your decisions—not replace professional medical advice.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link 
              to="/privacy" 
              className="text-amber-700 hover:text-amber-900 text-sm font-medium underline"
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms" 
              className="text-amber-700 hover:text-amber-900 text-sm font-medium underline"
            >
              Terms & Conditions
            </Link>
            <Link 
              to="/disclaimer" 
              className="text-amber-700 hover:text-amber-900 text-sm font-medium underline"
            >
              Medical Disclaimer
            </Link>
          </div>
        </motion.section>
      </main>

      <GlobalFooter />
    </div>
  );
}
